import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import type { LogistDriver, LogistRollingStock } from '@entities/logistics';
import { useCreateLogistSeriesMutation, useGenerateLogistSeriesMutation } from '@shared/api';

const Section = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  max-width: 900px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
  align-items: center;
`;

const Msg = styled.div<{ $error?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
  color: ${({ theme, $error }) =>
    $error ? theme.colors.status.danger : theme.colors.text.secondary};
`;

function apiErr(e: unknown): string {
  if (e && typeof e === 'object' && 'data' in e) {
    const d = (e as { data?: unknown }).data;
    if (d && typeof d === 'object' && 'error' in d) return String((d as { error: unknown }).error);
  }
  return 'ошибка запроса';
}

export interface LogistSeriesTemplateFormProps {
  orderId: string;
  drivers: LogistDriver[];
  vehicles: LogistRollingStock[];
  trailers: LogistRollingStock[];
  onSeriesCreated?: (message: string) => void;
}

export function LogistSeriesTemplateForm({
  orderId,
  drivers,
  vehicles,
  trailers,
  onSeriesCreated,
}: LogistSeriesTemplateFormProps) {
  const [form, setForm] = useState<Record<string, string>>({});
  const [ok, setOk] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [createSeries, { isLoading: creatingSeries }] = useCreateLogistSeriesMutation();
  const [generateSeries] = useGenerateLogistSeriesMutation();

  function f(key: string): string {
    return form[key] ?? '';
  }

  function setF(key: string, v: string) {
    setForm((s) => ({ ...s, [key]: v }));
  }

  async function submitSeries() {
    setErr(null);
    setOk(null);
    if (!orderId) {
      setErr('Выберите заказ.');
      return;
    }
    const count = Number(f('planned_trips_count'));
    if (!count || count < 1) {
      setErr('Число ходок — целое ≥ 1.');
      return;
    }
    const body: Record<string, unknown> = { order_id: orderId, planned_trips_count: count };
    if (f('driver_id')) body.driver_id = f('driver_id');
    if (f('vehicle_id')) body.vehicle_id = f('vehicle_id');
    if (f('trailer_id')) body.trailer_id = f('trailer_id');
    if (f('cargo_name')) body.cargo_name = f('cargo_name');
    if (f('loading_address')) body.loading_address = f('loading_address');
    if (f('unloading_address')) body.unloading_address = f('unloading_address');
    if (f('planned_mass_per_trip_kg')) {
      const m = Number(f('planned_mass_per_trip_kg'));
      if (Number.isNaN(m)) {
        setErr('Масса на ходку — число.');
        return;
      }
      body.planned_mass_per_trip_kg = m;
    }
    try {
      const res = await createSeries(body).unwrap();
      const gen = await generateSeries(res.series.id).unwrap();
      const successMsg = `Серия создана, сгенерировано рейсов: ${gen.generated}.`;
      setOk(successMsg);
      setForm({});
      onSeriesCreated?.(successMsg);
    } catch (e) {
      setErr(`Не удалось: ${apiErr(e)}`);
    }
  }

  return (
    <Section>
      <SectionTitle>Новая серия рейсов</SectionTitle>
      <Card>
        <Grid>
          <Field>
            <Label>Груз</Label>
            <Input value={f('cargo_name')} onChange={(e) => setF('cargo_name', e.target.value)} />
          </Field>
          <Field>
            <Label>Адрес погрузки</Label>
            <Input
              value={f('loading_address')}
              onChange={(e) => setF('loading_address', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Адрес выгрузки</Label>
            <Input
              value={f('unloading_address')}
              onChange={(e) => setF('unloading_address', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Водитель</Label>
            <Select value={f('driver_id')} onChange={(e) => setF('driver_id', e.target.value)}>
              <option value="">— не задан —</option>
              {drivers.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.full_name}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Транспортное средство</Label>
            <Select value={f('vehicle_id')} onChange={(e) => setF('vehicle_id', e.target.value)}>
              <option value="">— не задано —</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate_number}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Прицеп</Label>
            <Select value={f('trailer_id')} onChange={(e) => setF('trailer_id', e.target.value)}>
              <option value="">— не задан —</option>
              {trailers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.plate_number}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>Масса на ходку, кг</Label>
            <Input
              type="number"
              value={f('planned_mass_per_trip_kg')}
              onChange={(e) => setF('planned_mass_per_trip_kg', e.target.value)}
            />
          </Field>
          <Field>
            <Label>Число ходок *</Label>
            <Input
              type="number"
              value={f('planned_trips_count')}
              onChange={(e) => setF('planned_trips_count', e.target.value)}
            />
          </Field>
        </Grid>
        <Row>
          <Button onClick={submitSeries} disabled={creatingSeries}>
            Создать серию и сгенерировать рейсы
          </Button>
        </Row>
        {ok && <Msg>{ok}</Msg>}
        {err && <Msg $error>{err}</Msg>}
      </Card>
    </Section>
  );
}
